import { useEffect, useState } from 'react'

import { Table } from '@/componentsV1/BaseComponents'
import { getFetchError, getIsFetchingOrNotStarted } from '@/reducers/fetch'
import AnnualTaxDetailsForm from './components/AnnualTaxDetailsForm'
import AnnualTaxDetailsRow from './components/AnnualTaxDetailsRow'
import { selectAllAnnualTaxDetailsSorted } from './annualTaxDetails.selector'
import {
  AnnualTaxDetail,
  FETCH_ALL_ANNUAL_TAX_DETAILS_KEY,
  fetchAdminAllAnnualTaxDetailsIfNeeded,
} from './annualTaxDetails.slice'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { listAnnualTaxDateTypes } from './annualTaxDateTypes.slice'
import { Alert, Button, Dialog, Text } from '@/ui'
import { TypographyStyles } from '@/styles'

interface ModalVisibility {
  open: boolean
  annualTaxDetail?: AnnualTaxDetail
}

const AnnualTaxDetailsPanel = () => {
  const dispatch = useAppDispatch()
  const annualTaxDateTypes = useFetchResponse(listAnnualTaxDateTypes)
  // This bit is responsible for making the GET request; it fires whenever its dependency array changes,
  // (currently upon component load)
  useEffect(() => {
    dispatch(fetchAdminAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  // Creates a state variable with default value = false
  const [modalVisibility, setModalVisibility] = useState<ModalVisibility>({
    open: false,
    annualTaxDetail: undefined,
  })

  const allAnnualTaxDetailsSorted = useReselector(
    selectAllAnnualTaxDetailsSorted
  )

  const loading = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_ALL_ANNUAL_TAX_DETAILS_KEY
  )
  const error = useReselector(getFetchError, FETCH_ALL_ANNUAL_TAX_DETAILS_KEY)

  const openModal = (annualTaxDetail?: AnnualTaxDetail) =>
    setModalVisibility({ open: true, annualTaxDetail })
  const closeModal = () =>
    setModalVisibility({ open: false, annualTaxDetail: undefined })

  const renderError = (error: { message: string }) => (
    <Text>
      An error occurred, refresh page to try again. &quot;{error.message}&quot;
    </Text>
  )
  const renderLoading = () => <Text>loading...</Text>
  const renderMain = () => (
    <>
      <Dialog open={modalVisibility.open} onClose={closeModal}>
        <Dialog.Trigger asChild>
          <Button variant="secondary" onClick={() => openModal()}>
            Create a future entry
          </Button>
        </Dialog.Trigger>
        <Dialog.Content>
          <Dialog.Header>
            {modalVisibility.annualTaxDetail
              ? `Update ${modalVisibility.annualTaxDetail.taxYear}`
              : 'Create a future entry'}
          </Dialog.Header>

          <AnnualTaxDetailsForm
            onCancel={closeModal}
            details={modalVisibility.annualTaxDetail}
            dateTypes={annualTaxDateTypes}
          />
        </Dialog.Content>
      </Dialog>
      <Alert className="noBorder" title="All dates are inclusive">
        <Text>
          Changing values can impact Tax Center content. Please double-check
          your changes before applying.
        </Text>
      </Alert>
      <div className="mb-32 max-w-full overflow-x-auto">
        <Table collapsing>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>Tax Year</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              {annualTaxDateTypes?.map((dateType) => (
                <Table.HeaderCell key={dateType.id}>
                  {dateType.displayName}
                </Table.HeaderCell>
              ))}
              <Table.HeaderCell>Last Updated</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          {allAnnualTaxDetailsSorted.map((details) => (
            <AnnualTaxDetailsRow
              onEditClick={() => openModal(details)}
              key={`detail-${details.taxYear}`}
              details={details}
              dateTypes={annualTaxDateTypes}
            />
          ))}
        </Table>
      </div>
    </>
  )

  return (
    <>
      <Text variant={TypographyStyles.H1}>Annual Tax Details</Text>
      <br />
      {loading && renderLoading()}
      {!loading && !error && renderMain()}
      {error && renderError(error)}
    </>
  )
}

export default AnnualTaxDetailsPanel
